import React, { useCallback, useEffect, useMemo, useState } from "react";
import { type FieldErrors, type UseFormSetValue, useFormContext } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { ScrollView, StyleSheet } from "react-native";
import type { ReservationFormParams } from "../../store/draftReservation/types";
import { setHomeSize } from "../../store/homeProfile";

import {
  AnalyticEvent,
  type AutoCompleteResponseFragment,
  isNative,
  useDebounce,
  useGetAutoCompleteAddressQuery,
  usePostHog,
  useValidateAddressInLocalizationMutation,
} from "@stepapp/core";
import type { V2AddressUpsertInput } from "@stepapp/core/apiTypes";
import { Block, Input, Loader, Typography, palette, spacing } from "@stepapp/ui";

import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import AlreadyHaveAccount from "../AlreadyHaveAccount";
import { AutocompleteItem } from "../AutocompleteItem";
import LocationDisclaimerSheet from "../Modal/LocationDisclaimerSheet";

type Props = {
  houseArea: number;
  errors: FieldErrors<ReservationFormParams>;
  serviceHouseArea?: {
    id: string;
    enabled: boolean;
    steps: {
      to: number;
      from: number;
    }[];
  } | null;
  setValue: UseFormSetValue<ReservationFormParams>;
  address?: V2AddressUpsertInput | null;
};

const InitialMyHomeForm = ({ setValue, serviceHouseArea, errors, houseArea, address }: Props) => {
  const appDispatch = useAppDispatch();
  const postHog = usePostHog();
  const { t } = useTranslation();
  const { trigger } = useFormContext();
  const localizationId = useAppSelector(({ appConfig }) => appConfig.localizationId);
  const [selectedAddress, setSelectedAddress] = useState(address?.streetAddress1 || "");
  const [notAvailableLocation, setNotAvailableLocation] = useState(false);
  const [isAutocompleteDropdownOpen, setIsAutocompleteDropdownOpen] = useState(false);

  const isHomeSizeVisible = !!serviceHouseArea?.enabled;

  const debouncedAddress = useDebounce(selectedAddress, 500);

  const [validateAddress] = useValidateAddressInLocalizationMutation();
  const { data, loading } = useGetAutoCompleteAddressQuery({
    variables: { payload: { address: `${address?.city}, ${debouncedAddress}` } },
    skip: debouncedAddress?.length < 3,
  });

  const addresses = useMemo(
    () => data?.generateAutocompleteForAddress?.slice(0, 3) || [],
    [data?.generateAutocompleteForAddress],
  );

  const homeSizeValidation = useCallback(() => {
    if (houseArea >= 10) {
      postHog?.capture(AnalyticEvent.User.HomeSize, { size: houseArea });
      appDispatch(setHomeSize(houseArea));
    }

    trigger("houseArea");
  }, [houseArea, trigger, postHog, appDispatch]);

  const handleAddressPress = useCallback(
    async (autocompleteAddress: AutoCompleteResponseFragment) => {
      const { data } = await validateAddress({
        variables: {
          payload: {
            city: autocompleteAddress?.city || "",
            localizationId,
            postalOrZipCode: autocompleteAddress?.postalCode || "",
            streetAddress1: autocompleteAddress?.address || "",
            streetAddress2: "",
          },
        },
      });
      if (!data?.validateAddressInLocalization) {
        setValue("address.streetAddress1", "");
        setIsAutocompleteDropdownOpen(false);
        return setNotAvailableLocation(true);
      }

      setSelectedAddress(autocompleteAddress?.address || "");
      setValue(
        "address",
        {
          city: autocompleteAddress?.city || address?.city || "",
          floorArea: address?.floorArea || 0,
          postalOrZipCode: autocompleteAddress?.postalCode || "",
          streetAddress1: autocompleteAddress?.address || "",
          streetAddress2: "",
          notes: "",
          type: address?.type,
        },
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );

      setIsAutocompleteDropdownOpen(false);
    },
    [address?.city, address?.floorArea, address?.type, localizationId, setValue, validateAddress],
  );

  const handleSizeChange = useCallback(
    (value: string) => {
      const onlyNumbers = value.replace(/[^0-9]/g, "");
      const newHouseAreaValue = Number.parseFloat(onlyNumbers) || 0;

      setValue("address.floorArea", newHouseAreaValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("houseArea", newHouseAreaValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  useEffect(() => {
    if (isNative) return;

    function onOutsideClick(e: ToDo) {
      if (e.target.id === "addressAutocomplete") return;

      setIsAutocompleteDropdownOpen(false);
      setSelectedAddress(address?.streetAddress1 || "");
    }

    document?.addEventListener("click", onOutsideClick);

    return () => document?.removeEventListener("click", onOutsideClick);
  }, [address?.streetAddress1]);

  return (
    <>
      {isHomeSizeVisible && (
        <>
          <Block marginTop={3} paddingBottom={1}>
            <Typography variant="button">{t("screens.homeEdit.surface")}</Typography>
          </Block>
          <Input
            testID="initialHomeSize"
            error={errors?.houseArea?.message}
            keyboardType="number-pad"
            onBlur={homeSizeValidation}
            onChangeText={handleSizeChange}
            placeholder={t("components.input.placeholder.size")}
            suffixComponent={() => (
              <Typography type="secondary" variant="paragraph">
                {t("screens.flatSize.unit")}
              </Typography>
            )}
            value={`${houseArea}`}
          />
        </>
      )}

      <Block marginTop={isHomeSizeVisible ? 0 : 3} paddingBottom={1}>
        <Typography variant="button">{t("pages.serviceDateAndAddress.addAddress")}</Typography>
      </Block>

      <Block style={styles.autocompleteContainer}>
        <Input
          error={errors?.address?.streetAddress1?.message}
          id="addressAutocomplete"
          testID="addressAutocomplete"
          onChangeText={(v) => setSelectedAddress(v)}
          onFocus={() => setIsAutocompleteDropdownOpen(true)}
          placeholder={t("components.input.placeholder.typeAddress")}
          value={selectedAddress}
        />

        <Block marginTop={isHomeSizeVisible ? 0 : 3} paddingBottom={1}>
          <Typography type="secondary" variant="lightLabel">
            <Trans t={t} i18nKey="screens.service.autocompleteInfo">
              <Typography type="secondary" variant="label">
                Enter the full street name
              </Typography>{" "}
              to check the availability of our services. In the next steps, you can add additional
              information.
            </Trans>
          </Typography>
        </Block>

        {isAutocompleteDropdownOpen &&
          (loading ? (
            <Block alignCenter paddingTop={2}>
              <Loader />
            </Block>
          ) : (
            <ScrollView style={styles.addressDropdown} testID="autocompleteDropdown">
              {addresses.map((address, index) => (
                <AutocompleteItem
                  {...address}
                  divider={index !== addresses.length - 1}
                  key={address.latitude}
                  onPress={() => handleAddressPress(address)}
                />
              ))}
              {addresses.length > 0 && <AlreadyHaveAccount />}
            </ScrollView>
          ))}
      </Block>

      <LocationDisclaimerSheet
        isVisible={notAvailableLocation}
        setModalVisible={setNotAvailableLocation}
      />
    </>
  );
};

export default React.memo(InitialMyHomeForm);

const styles = StyleSheet.create({
  addressDropdown: {
    backgroundColor: palette.common.white,
    borderRadius: spacing.radius,
    boxShadow: "2px 2px 10px 0px rgba(0, 0, 0, 0.2)",
    maxHeight: spacing.unit * 35,
    paddingHorizontal: spacing.unit * 2,
    position: "absolute",
    top: spacing.unit * 8,
    width: "100%",
  },
  autocompleteContainer: {
    position: "relative",
    zIndex: 100,
  },
});
